import React, { useEffect } from 'react';
import type { OAuthResult } from './vcs-connection-modal-form-oauth-authorization-button';
import VcsConnectionModalFormOauthAuthorizationButton from './vcs-connection-modal-form-oauth-authorization-button';
import { Row } from 'components/common/grid';
import { Typography } from 'antd';
import Alert from 'components/common/feedback/alert';
import { FormattedMessage } from 'react-intl';
import type { VcsConnectionsForm } from './access-card/use-connection-modal-access-card-form.hook';
import type { VcsApi } from '@env0/vcs-service/api';
import { getAzureDevOpsAuthenticationTypeInUse } from './access-card/supported-vcs-connections-common';

export const VcsConnectionModalFormOauth: React.FC<VcsConnectionsForm> = ({
  vcsConnection,
  form,
  onTokenProcessingChange,
  onAlreadySavedChange
}) => {
  const [oauthResult, setOauthResult] = React.useState<OAuthResult>({});
  const authenticationType = form.watch('authenticationType') as VcsApi.OAuthAuthenticationType;
  // Read once: the refetch after a switch updates the connection, which would turn "Switched to" into a re-grant
  const [authenticationTypeInUse] = React.useState(() => getAzureDevOpsAuthenticationTypeInUse(vcsConnection));
  // The popup rewrites the token an Azure DevOps connection points at, so a grant on one is already saved
  const getSuccessMessageSuffix = () => {
    if (oauthResult.isAlreadySaved) return '.alreadySaved';
    if (!authenticationTypeInUse) return '';
    return authenticationType === authenticationTypeInUse ? '.AzureDevOps' : `.AzureDevOps.${authenticationType}`;
  };

  const [, setAuthorizationId] = form.useManualField('authorizationId');
  const [, setTokenId] = form.useManualField('tokenId');

  useEffect(() => {
    onAlreadySavedChange?.(oauthResult.isAlreadySaved ? oauthResult.accessScope : undefined);
    if (oauthResult.error) {
      setAuthorizationId(undefined);
      setTokenId(undefined);
    } else if (oauthResult.tokenId) {
      setTokenId(oauthResult.tokenId);
      setAuthorizationId(undefined);
    } else if (oauthResult.authorizationId) {
      setAuthorizationId(oauthResult.authorizationId);
      setTokenId(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oauthResult]);

  return (
    <>
      <Row style={{ marginBottom: '7px' }}>
        <Alert
          type="warning"
          message={
            <FormattedMessage id="organization.settings.agents.vcs-connections-modal.connection.oauth.warning" />
          }
        />
      </Row>
      <Row>
        <VcsConnectionModalFormOauthAuthorizationButton
          connectionType={vcsConnection.connectionType}
          vcsType={vcsConnection.type}
          authenticationType={authenticationType}
          onClosed={setOauthResult}
          onTokenProcessingChange={onTokenProcessingChange}
          accessScope={vcsConnection.accessScope}
        />
      </Row>
      <Row style={{ marginBottom: '4px' }}>
        {oauthResult.error && (
          <Typography.Text strong underline type={'danger'}>
            <FormattedMessage
              id="organization.settings.agents.vcs-connections-modal.connection.oauth.failed"
              values={{ message: oauthResult.error }}
            />
          </Typography.Text>
        )}
        {oauthResult.accessScope && (
          <Typography.Text strong underline type={'success'}>
            <FormattedMessage
              id={`organization.settings.agents.vcs-connections-modal.connection.oauth.success${getSuccessMessageSuffix()}`}
              values={{ accessScope: oauthResult.accessScope }}
            />
          </Typography.Text>
        )}
      </Row>
    </>
  );
};
